import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Gift, Loader2, Stamp, Star, CheckCircle2 } from 'lucide-react';

export default function JoinCardPage() {
    const { cardId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [card, setCard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);
    const [joined, setJoined] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/cards/${cardId}`)
            .then(res => setCard(res.data))
            .catch(() => setError('Programme de fidélité introuvable.'))
            .finally(() => setLoading(false));
    }, [cardId]);

    const handleJoin = async () => {
        if (!user) {
            localStorage.setItem('fidelrun_join_card', cardId);
            navigate('/inscription');
            return;
        }
        if (user.role !== 'client') {
            toast.error('Seuls les clients peuvent rejoindre un programme.');
            return;
        }
        setJoining(true);
        try {
            await api.post(`/wallet/join/${cardId}`);
            setJoined(true);
            toast.success('Vous avez rejoint le programme !');
        } catch (err) {
            const msg = err.response?.data?.detail || 'Erreur';
            if (msg === 'Deja membre') {
                setJoined(true);
                toast.info('Vous êtes déjà membre de ce programme.');
            } else {
                toast.error(msg);
            }
        } finally {
            setJoining(false);
        }
    };

    // Auto-join after login/register if redirected
    useEffect(() => {
        const pendingCard = localStorage.getItem('fidelrun_join_card');
        if (user && user.role === 'client' && pendingCard === cardId && !joined) {
            localStorage.removeItem('fidelrun_join_card');
            handleJoin();
        }
    }, [user]);

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
    );

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
            <Card className="max-w-sm w-full rounded-2xl">
                <CardContent className="p-8 text-center space-y-4">
                    <Gift className="w-12 h-12 text-slate-300 mx-auto" />
                    <p className="text-slate-600">{error}</p>
                    <Link to="/"><Button variant="outline">Retour à l'accueil</Button></Link>
                </CardContent>
            </Card>
        </div>
    );

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
            <Card className="max-w-sm w-full rounded-2xl overflow-hidden" data-testid="join-card-page">
                {/* Card preview header */}
                <div className="h-32 relative" style={{ background: `linear-gradient(135deg, ${card.card_color_from || '#059669'}, ${card.card_color_to || '#047857'})` }}>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center text-white">
                            <Gift className="w-8 h-8 mx-auto mb-2 opacity-90" />
                            <p className="text-lg font-bold">{card.merchant_name}</p>
                        </div>
                    </div>
                </div>

                <CardContent className="p-6 space-y-5">
                    <div className="text-center">
                        <h1 className="text-xl font-bold text-slate-900">{card.name}</h1>
                        <p className="text-sm text-slate-500 mt-1">
                            {card.type === 'stamp' && <><Stamp className="w-3.5 h-3.5 inline mr-1" />{card.stamp_goal} tampons pour une récompense</>}
                            {card.type === 'points' && <><Star className="w-3.5 h-3.5 inline mr-1" />{card.points_per_euro} point(s) par euro</>}
                        </p>
                    </div>

                    {card.reward_description && (
                        <div className="bg-emerald-50 rounded-xl p-3 text-center">
                            <p className="text-xs text-emerald-600 font-medium">Récompense</p>
                            <p className="text-sm font-semibold text-emerald-800">{card.reward_description}</p>
                        </div>
                    )}

                    {joined ? (
                        <div className="text-center space-y-3" data-testid="join-success">
                            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                            <p className="font-semibold text-emerald-700">Vous êtes membre !</p>
                            <Button onClick={() => navigate('/client')} className="bg-emerald-500 hover:bg-emerald-600 text-white w-full" data-testid="go-to-wallet">
                                Voir mon wallet
                            </Button>
                        </div>
                    ) : (
                        <Button onClick={handleJoin} disabled={joining} className="bg-emerald-500 hover:bg-emerald-600 text-white w-full h-11 font-semibold" data-testid="join-card-btn">
                            {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Rejoindre le programme'}
                        </Button>
                    )}

                    {!user && !joined && (
                        <p className="text-xs text-center text-slate-400">
                            Vous avez déjà un compte ? <Link to="/connexion" className="text-emerald-600 font-medium hover:underline">Se connecter</Link>
                        </p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
